/**
 * Sticky add-to-cart bar (mobile).
 *
 * Shows once the real add-to-cart button has scrolled out of view, and forwards
 * to it rather than owning a form of its own. That matters: scripts.js patches
 * the product page by copying innerHTML between elements with matching ids after
 * every variant change, so a second `price-{section}` or `product-form-{section}`
 * would break price and stock updates silently. The bar holds no id of its own
 * and mirrors the real price element instead.
 *
 * Mirroring happens on connect, on `product:variant-change` (dispatched on
 * document by scripts.js), and on any mutation of the price element, which covers
 * the innerHTML patch that fires on variant change without a matching event.
 */
class ProductStickyBar extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.priceTarget = this.querySelector("[data-sticky-price]");
    this.addButton = this.querySelector("[data-sticky-add]");

    this.realPrice = document.getElementById(`price-${this.sectionId}`);
    this.realForm = document.getElementById(`product-form-${this.sectionId}`);
    this.realAdd = this.realForm?.querySelector('[name="add"]');

    // Nothing to forward to: leave the bar hidden rather than render a dead button.
    if (!this.realAdd) return;

    this.onVariantChange = this.syncFromProduct.bind(this);
    document.addEventListener("product:variant-change", this.onVariantChange);
    // The button's line total (quantity × price with options) is recomputed by
    // dc-product-buybox.js, including just after a variant change, so follow it.
    document.addEventListener("dc:line-total", this.onVariantChange);

    this.addButton.addEventListener("click", this.handleClick.bind(this));

    this.observePrice();
    this.observeAnchor();
    this.syncFromProduct();
  }

  disconnectedCallback() {
    document.removeEventListener("product:variant-change", this.onVariantChange);
    document.removeEventListener("dc:line-total", this.onVariantChange);
    this.priceObserver?.disconnect();
    this.anchorObserver?.disconnect();
  }

  /** Copy the live price and the real button's label and disabled state. */
  syncFromProduct() {
    if (this.realPrice && this.priceTarget) {
      this.priceTarget.innerHTML = this.realPrice.innerHTML;
    }

    if (this.realAdd) {
      const disabled = this.realAdd.hasAttribute("disabled");
      this.addButton.toggleAttribute("disabled", disabled);

      const label = this.realAdd.querySelector("span") || this.realAdd;
      if (label.textContent.trim()) {
        this.addButton.textContent = label.textContent.trim();
      }
    }
  }

  /**
   * scripts.js replaces the price with innerHTML and fires no event of its own
   * for it, so watch the element directly. Cheap: one subtree observer on a node
   * that changes only on variant switches.
   */
  observePrice() {
    if (!this.realPrice) return;

    this.priceObserver = new MutationObserver(() => this.syncFromProduct());
    this.priceObserver.observe(this.realPrice, { childList: true, subtree: true });
  }

  /**
   * Reveal the bar once the real button leaves the viewport upwards. Matching the
   * StickyCollectionActions pattern in facets.js: boundingClientRect.top tells
   * scroll direction, so the bar does not appear when the button is still below.
   */
  observeAnchor() {
    this.anchorObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.hidden = !scrolledPast;
      },
      { threshold: 0 },
    );

    this.anchorObserver.observe(this.realAdd);
  }

  /** Click the real button so product-form's own submit handler runs. */
  handleClick(event) {
    event.preventDefault();
    if (this.addButton.hasAttribute("disabled")) return;
    this.realAdd?.click();
  }
}

customElements.define("product-sticky-bar", ProductStickyBar);
